import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { ToastService } from './toast.service';
import { API_URL } from '../../../app.config';
import { catchError } from 'rxjs/operators';
import { AnswerSearch } from '../models/recent-search';
import { RootResponseOpenSearch } from '../models/response-open-search';
import { TranslatableField } from '../models/TranslatableField';
import { ActiveTooltipData } from '../models/ActiveTooltipMarkdownText';
import { RecentSearchModel } from '../models/recentSearchModel';
import { Subject, takeUntil } from 'rxjs';
import { ABORT_SIGNAL } from '../../interceptors/abort-signal.interceptor';

@Injectable({
  providedIn: 'root',
})
export class OpenSearchService {
  private readonly apiUrl = inject(API_URL);
  private http = inject(HttpClient);
  private toastService = inject(ToastService);
  readonly _promts = signal<AnswerSearch[] | null>(null);
  private readonly _categories = signal<any[] | null>(null);
  private _activeTooltipUrl = signal<ActiveTooltipData | null>(null);
  _historyResponse = signal<RootResponseOpenSearch | null>(null);
  _dataOpenLogic = signal<null | {
    showAllResults: boolean;
    activeDestroyComponent: boolean;
    showResponseView: boolean;
  }>(null);
  private _isNewSearch = signal<boolean>(false);
  private _conversationIdSignal = signal<string | null>(null);
  private _spinnerConversational = signal<boolean>(false);
  inputText = signal<string>('');
  private _pageRecentSearch = signal<number>(1);
  private _reacentSearches = signal<RecentSearchModel[]>([]);
  private _conversations = signal<RootResponseOpenSearch[]>([]);
  private _errorDrawer = signal<string | null>(null);
  openedPanelFromResponse = signal(false);
  private _cancelRequest$ = new Subject<void>();
  private _currentAbortController: AbortController | null = null;

  get promts() {
    return this._promts;
  }

  get categories() {
    return this._categories;
  }
  get spinnerConversational() {
    return this._spinnerConversational;
  }

  get activeTooltipUrl() {
    return this._activeTooltipUrl;
  }

  get historyResponse() {
    return this._historyResponse;
  }
  get errorDrawer() {
    return this._errorDrawer;
  }
  get isNewSearch() {
    return this._isNewSearch;
  }
  get conversationIdSignal() {
    return this._conversationIdSignal;
  }

  get pageRecentSearch() {
    return this._pageRecentSearch;
  }

  get recentSearches() {
    return this._reacentSearches;
  }

  get conversations() {
    return this._conversations;
  }

  getCategories(): void {
    this.http
      .get<any[]>(`${this.apiUrl}QuickSearch/GetPromptsCategories`)
      .pipe(
        catchError(error => {
          this.toastService.show(error.message, 'error');
          throw error;
        }),
      )
      .subscribe(categoriesData => {
        this._categories.set(categoriesData);
      });
  }

  getPromts(category?: number, favorite?: boolean): void {
    this._promts.set([]);
    const params: any = {};
    if (category !== undefined) params.category = category;
    if (favorite !== undefined) params.favorite = favorite;

    this.http
      .get<any[]>(`${this.apiUrl}QuickSearch/GetPrompts`, { params })
      .pipe(
        catchError(error => {
          this.toastService.show(error.message, 'error');
          throw error;
        }),
      )
      .subscribe(promtsData => {
        this._promts.set(
          promtsData.map(c => ({
            value: c.favorite,
            text: c.text,
            selected: false,
          })),
        );
      });
  }

  cancelRequest() {
    if (this._currentAbortController) {
      this._currentAbortController.abort();
      this._currentAbortController = null;
    }
    this._cancelRequest$.next();
  }

  postDrawer(promptxt: string, modeType?: string) {
    this.cancelRequest();
    this._currentAbortController = new AbortController();
    const abortSignal = this._currentAbortController.signal;
    this.spinnerConversational.set(true);
    this.errorDrawer.set(null);
    const prompt = btoa(encodeURIComponent(promptxt));
    this.resetIsNewSearch();
    this.http
      .post<RootResponseOpenSearch>(
        `${this.apiUrl}OpenQuery/GetOpenQuery`,
        {
          prompt,
          conversationId: this.normalizeConversationId(this.conversationIdSignal()),
          ...(modeType ? { modeType } : {}),
        },
        {
          observe: 'response',
          context: new HttpContext().set(ABORT_SIGNAL, abortSignal),
        },
      )
      .pipe(
        takeUntil(this._cancelRequest$),
        catchError(error => {
          if (error?.name === 'AbortError' || error?.status === 0) {
            this.spinnerConversational.set(false);
            throw error;
          }
          this.toastService.show(error.message, 'error');
          this.spinnerConversational.set(false);
          this._isNewSearch.set(true);
          this.errorDrawer.set(error.message);
          this.appendFailedInteraction(promptxt);
          throw error;
        }),
      )
      .subscribe({
        next: httpResponse => {
          this._currentAbortController = null;
          this.spinnerConversational.set(false);
          this._isNewSearch.set(true);
          const body = httpResponse.body!;
          if (httpResponse.status === 200) {
            this.conversationIdSignal.set(this.normalizeConversationId(body.id));
            this.setHistoryResponseData(this.preserveInteractionModes(body, modeType ?? 'default'));
            return;
          }
          if (httpResponse.status === 204) {
            this.appendFailedInteraction(promptxt);
          }
        },
        error: () => {
          this._currentAbortController = null;
        },
      });
  }

  private preserveInteractionModes(
    response: RootResponseOpenSearch,
    requestedModeType: string,
  ): RootResponseOpenSearch {
    const previousInteractions = this.historyResponse()?.openQueryInteractions ?? [];
    const interactions = response.openQueryInteractions.map((interaction, index, all) => {
      const previous = previousInteractions[index];
      const isCurrentInteraction = index === all.length - 1;
      const interactionMode = isCurrentInteraction
        ? requestedModeType
        : previous?.modeType ?? interaction.modeType;
      const interactionModeResponse = isCurrentInteraction
        ? response.modeResponse ?? previous?.modeResponse
        : previous?.modeResponse ?? interaction.modeResponse;

      return {
        ...interaction,
        ...(interactionMode ? { modeType: interactionMode } : {}),
        ...(interactionModeResponse ? { modeResponse: interactionModeResponse } : {}),
      };
    });

    return { ...response, openQueryInteractions: interactions };
  }

  private normalizeConversationId(id: string | null | undefined): string | null {
    return id?.trim() ? id : null;
  }

  private appendFailedInteraction(promptxt: string): void {
    const currentData = this.historyResponse();
    const currentId = this.normalizeConversationId(this.conversationIdSignal() ?? currentData?.id) ?? '';
    const previousInteractions = currentData?.openQueryInteractions ?? [];
    const responseData: RootResponseOpenSearch = {
      ...currentData,
      id: currentId,
      userId: currentData?.userId ?? '',
      countConversations: currentData?.countConversations ?? 0,
      openQueryInteractions: [
        ...previousInteractions,
        {
          question_id: undefined,
          question: promptxt,
          response: null,
          sources: [],
          limitQuestions: false,
        },
      ],
    };
    this.conversationIdSignal.set(this.normalizeConversationId(responseData.id));
    this.setHistoryResponseData(responseData);
  }

  setHistoryResponseData(data?: RootResponseOpenSearch, index?: number) {
    const response = data || this.historyResponse()!;
    if (response.openQueryInteractions && response.openQueryInteractions.length > 0) {
      const lastIdx = response.openQueryInteractions.length - 1;
      const activeIdx = index ?? lastIdx;
      response.openQueryInteractions = response.openQueryInteractions.map((item, idx) => {
        const isActive = idx === activeIdx;
        const complement = item.responseModeComplement;
        const modeType = item.modeType ?? complement?.modeType ?? (isActive ? response.modeType : undefined);
        const modeResponse =
          item.modeResponse ?? complement?.modeResponse ?? (isActive ? response.modeResponse : undefined);
        return {
          ...item,
          ...(modeType ? { modeType } : {}),
          ...(modeResponse ? { modeResponse } : {}),
          isActive,
        };
      });
    }
    this._historyResponse.set(response);
  }

  resetIsNewSearch() {
    [0, 10, 50].forEach(delay => {
      setTimeout(() => {
        this._isNewSearch.set(false);
      }, delay);
    });
  }

  convertTextToTranslateItem = (value: string | TranslatableField): TranslatableField =>
    typeof value === 'string'
      ? {
          en: value,
          es: value,
          pt: value,
          fr: value,
        }
      : value;
  setActiveTooltip = (data: ActiveTooltipData) => this._activeTooltipUrl.set(data);
  clearActiveTooltip = () => this._activeTooltipUrl.set(null);

  getRecentSeaeches() {
    this.http
      .get<RecentSearchModel>(
        `${this.apiUrl}OpenQuery/GetUserOpenQueries/page/${this.pageRecentSearch()}/size/10`,
        {},
      )
      .pipe(
        catchError(error => {
          this.toastService.show(error.message, 'error');
          throw error;
        }),
      )
      .subscribe((data: RecentSearchModel) => {
        this._reacentSearches.set([data]);
      });
  }

  postConversations(conversationId?: string) {
    this._isNewSearch.set(false);
    this._historyResponse.set(null);
    const id = conversationId;
    this.http
      .post<RootResponseOpenSearch>(`${this.apiUrl}OpenQuery/PostOpenQuery`, {
        id: id,
      })
      .pipe(
        catchError(error => {
          this.toastService.show(error.message, 'error');
          this.spinnerConversational.set(false);
          this._isNewSearch.set(true);
          throw error;
        }),
      )
      .subscribe((data: RootResponseOpenSearch) => {
        this.conversationIdSignal.set(data.id);
        this.setHistoryResponseData(data);
        this._isNewSearch.set(true);
      });
  }
}
